import moment from "moment";
import { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  useLazyViewLatestMeterSnapshotQuery,
  useLazyViewMeterHistoryQuery,
  useLazyViewMeterImportExportQuery,
  useLazyViewMeterTimeOffUseQuery,
} from "../../../../../redux/api/meter/meterAPI";
import {
  selectMeterDashboardFilters,
  selectMeterDevice,
} from "../../../../../redux/features/meter/meter-slice";
import { selectProfile } from "../../../../../redux/features/user/user-slice";
import { IDataPointDTO } from "../../../../../shared/oversight-core/dtos/response-dtos/data-point-dto";
import IHistoryResponseDTO from "../../../../../shared/oversight-core/dtos/response-dtos/history-response-dto";
import IImportExportResponseDTO from "../../../../../shared/oversight-core/dtos/response-dtos/import-export-response-dto";
import ILatestSnapshotResponseDTO from "../../../../../shared/oversight-core/dtos/response-dtos/latest-snapshot-response-dto";
import ITimeOfUseResponseDTO from "../../../../../shared/oversight-core/dtos/response-dtos/time-of-use-response-dto";
import { EDateTypes } from "../../../../../shared/oversight-core/enums/date-types";
import { UserRole } from "../../../../../shared/oversight-core/enums/user-roles";
import BillInfoCard from "../../../../../shared/oversight-core/shared-components/bill-info-card/bill-info-card";
import ImportExportInfoCard from "../../../../../shared/oversight-core/shared-components/import-export-info-card/import-export-info-card";
import MeterInfoCard from "../../../../../shared/oversight-core/shared-components/meter-info-card/meter-info-card";
import ActiveInactiveIndicator from "../../../../../shared/oversight-core/ui-elements/active-inactive-indicator/active-inactive-indicator";
import ArrowButton from "../../../../../shared/oversight-core/ui-elements/arrow-button/arrow-button";
import BarChart from "../../../../../shared/oversight-core/ui-elements/bar-chart/bar-chart";
import ButtonWithBadge from "../../../../../shared/oversight-core/ui-elements/buttons/button-with-badge/button-with-badge";
import SpinnerModal from "../../../../../shared/oversight-core/ui-elements/spinner/spinner";
import FilterMeterDashboardSidePanel from "../filter-meter-dashboard-side-panel/filter-meter-dashboard-side-panel";
import LiveMeterComponent from "../live-meter-component/live-meter-component";
import RoleMeterUsageByTime from "../role-meter-usage-by-time/role-meter-usage-by-time";

const RoleMeterReaderDashboard = () => {
  const navigate = useNavigate();

  const meter = useSelector(selectMeterDevice);
  const profile = useSelector(selectProfile);
  const meterDashboardFilterStore = useSelector(selectMeterDashboardFilters);

  const [meterTimeOfUse, setMeterTimeOfUse] = useState<ITimeOfUseResponseDTO>();
  const [meterImportExport, setMeterImportExport] =
    useState<IImportExportResponseDTO>();
  const [latestMeterSnapshot, setLatestMeterSnapshot] =
    useState<IDataPointDTO[]>();
  const [lastUpdateDate, setLastUpdateDate] = useState<number>();
  const [dayDaily, setDayDaily] = useState<number[]>([]);
  const [peakDaily, setPeakDaily] = useState<number[]>([]);
  const [offPeakDaily, setOffPeakDaily] = useState<number[]>([]);
  const [chartLabels, setChartLabels] = useState<string[]>([]);
  const [showFilter, setShowFilter] = useState(false);

  const [triggerViewMeterTimeOfUse, { isFetching: isFetchingMeterTimeOfUse }] =
    useLazyViewMeterTimeOffUseQuery();
  const [
    triggerViewLatestMeterSnapshot,
    { isFetching: isFetchingViewLatestMeterSnapshot },
  ] = useLazyViewLatestMeterSnapshotQuery();
  const [
    triggerViewMeterImportExport,
    { isFetching: isFetchingViewMeterImportExport },
  ] = useLazyViewMeterImportExportQuery();
  const [triggerViewMeterHistory, { isFetching: isFetchingViewMeterHistory }] =
    useLazyViewMeterHistoryQuery();

  useEffect(() => {
    if (meter.deviceId) {
      triggerViewMeterTimeOfUse({
        deviceId: meter.deviceId,
      })
        .unwrap()
        .then((res: ITimeOfUseResponseDTO) => {
          setMeterTimeOfUse(res);
        })
        .catch(() => {
          setMeterTimeOfUse(undefined);
        });

      triggerViewLatestMeterSnapshot({
        deviceId: meter.deviceId,
      })
        .unwrap()
        .then((res: ILatestSnapshotResponseDTO) => {
          setLastUpdateDate(res.timestamp);
          setLatestMeterSnapshot(res.dataPoints);
        })
        .catch(() => {
          setLatestMeterSnapshot([]);
        });

      triggerViewMeterImportExport({
        deviceId: meter.deviceId,
      })
        .unwrap()
        .then((res: IImportExportResponseDTO) => {
          setMeterImportExport(res);
        })
        .catch(() => {
          setMeterImportExport(undefined);
        });
    }
  }, [
    triggerViewMeterTimeOfUse,
    triggerViewMeterImportExport,
    triggerViewLatestMeterSnapshot,
    meter.deviceId,
  ]);

  useEffect(() => {
    if (meter.deviceId) {
      let fromDate = moment(meterDashboardFilterStore.selectedDate)
        .startOf("month")
        .valueOf();
      let toDate = moment(meterDashboardFilterStore.selectedDate)
        .endOf("month")
        .valueOf();

      if (meterDashboardFilterStore.selectedDateType === EDateTypes.DAILY) {
        fromDate = moment(meterDashboardFilterStore.selectedDate)
          .startOf("day")
          .valueOf();
        toDate = moment().valueOf();
      } else if (
        meterDashboardFilterStore.selectedDateType === EDateTypes.WEEKLY
      ) {
        fromDate = moment(meterDashboardFilterStore.selectedDate)
          .subtract(7, "days")
          .startOf("day")
          .valueOf();
        toDate = moment().valueOf();
      } else if (
        meterDashboardFilterStore.selectedDateType === EDateTypes.MONTHLY
      ) {
        fromDate = moment(meterDashboardFilterStore.selectedDate)
          .startOf("month")
          .valueOf();
        toDate = moment(meterDashboardFilterStore.selectedDate)
          .endOf("month")
          .valueOf();
      }

      triggerViewMeterHistory({
        from: fromDate,
        to: toDate,
        deviceId: meter.deviceId,
      })
        .unwrap()
        .then((res: IHistoryResponseDTO) => {
          const readings = [...res.readings].sort(
            (a, b) => a.timestamp - b.timestamp,
          );

          const aggregatedReadings = readings.reduce(
            (
              acc: {
                label: string;
                dayCumulativeKwh: number;
                peakCumulativeKwh: number;
                offPeakCumulativeKwh: number;
              }[],
              current,
            ) => {
              const timestamp = moment
                .utc(current.timestamp)
                .utcOffset("+05:30");

              let label = "";
              if (
                meterDashboardFilterStore.selectedDateType === EDateTypes.DAILY
              ) {
                label = timestamp.format("HH:mm");
              } else if (
                meterDashboardFilterStore.selectedDateType ===
                EDateTypes.MONTHLY
              ) {
                label = timestamp.format("MM-DD");
              } else {
                label = timestamp.format("DD");
              }

              const existing = acc.find((item) => item.label === label);
              if (existing) {
                existing.dayCumulativeKwh += current.dayCumulativeKwh || 0;
                existing.peakCumulativeKwh += current.peakCumulativeKwh || 0;
                existing.offPeakCumulativeKwh +=
                  current.offPeakCumulativeKwh || 0;
              } else {
                acc.push({
                  label,
                  dayCumulativeKwh: current.dayCumulativeKwh || 0,
                  peakCumulativeKwh: current.peakCumulativeKwh || 0,
                  offPeakCumulativeKwh: current.offPeakCumulativeKwh || 0,
                });
              }
              return acc;
            },
            [],
          );

          const timeStamps = aggregatedReadings.map((item) => item.label);

          setChartLabels(timeStamps);
          setDayDaily(
            aggregatedReadings.map((dc) =>
              Number(dc.dayCumulativeKwh.toFixed(2)),
            ),
          );
          setPeakDaily(
            aggregatedReadings.map((dc) =>
              Number(dc.peakCumulativeKwh.toFixed(2)),
            ),
          );
          setOffPeakDaily(
            aggregatedReadings.map((dc) =>
              Number(dc.offPeakCumulativeKwh.toFixed(2)),
            ),
          );
        })
        .catch(() => {
          setChartLabels([]);
        });
    }
  }, [triggerViewMeterHistory, meter.deviceId, meterDashboardFilterStore]);

  return (
    <>
      {profile?.role !== UserRole.ROLE_METER_READER_USER && (
        <Row className="mx-0 mb-4">
          <Col>
            <ArrowButton onBack={() => navigate(-1)} />
          </Col>
        </Row>
      )}
      <Row>
        <Col>
          <RoleMeterUsageByTime
            isShowDatePicker={false}
            isLoading={isFetchingMeterTimeOfUse}
            day={meterTimeOfUse?.dayPct || 0}
            peak={meterTimeOfUse?.peakPct || 0}
            offPeak={meterTimeOfUse?.offPeakPct || 0}
          />
        </Col>
      </Row>
      <Row className="mt-4">
        <Col md={6} xl={3} className="d-flex align-items-stretch">
          <MeterInfoCard
            title="Meter Info"
            maxDemand={Number(
              latestMeterSnapshot?.find((ms) => ms.id === 32)?.value,
            )}
            phaseAVoltage={Number(
              latestMeterSnapshot?.find((ms) => ms.id === 1)?.value,
            )}
            phaseBVoltage={Number(
              latestMeterSnapshot?.find((ms) => ms.id === 2)?.value,
            )}
            phaseCVoltage={Number(
              latestMeterSnapshot?.find((ms) => ms.id === 3)?.value,
            )}
            phaseACurrent={Number(
              latestMeterSnapshot?.find((ms) => ms.id === 7)?.value,
            )}
            phaseBCurrent={Number(
              latestMeterSnapshot?.find((ms) => ms.id === 8)?.value,
            )}
            phaseCCurrent={Number(
              latestMeterSnapshot?.find((ms) => ms.id === 9)?.value,
            )}
            isLoading={isFetchingViewLatestMeterSnapshot}
          />
        </Col>
        <Col md={6} xl={3} className="d-flex align-items-stretch mt-2 mt-md-0">
          <ImportExportInfoCard
            title="Export"
            unit="Unit"
            icon="upload_2"
            iconColor="#3A9D49"
            iconBackgroundColor="#F5FFEE"
            value={meterImportExport?.totalExportKwh || 0}
            valueText="Total Export"
            day={meterImportExport?.phaseAExportKwh || 0}
            peak={meterImportExport?.phaseBExportKwh || 0}
            offPeak={meterImportExport?.phaseCExportKwh || 0}
            isLoading={isFetchingViewMeterImportExport}
            userRole={UserRole.ROLE_METER_READER_USER}
          />
        </Col>
        <Col md={6} xl={3} className="d-flex align-items-stretch mt-2 mt-xl-0">
          <ImportExportInfoCard
            title="Import"
            unit="Unit"
            icon="download_2"
            iconColor="#B56B4C"
            iconBackgroundColor="#FEF5ED"
            value={meterImportExport?.totalImportKwh || 0}
            valueText="Total Import"
            day={meterImportExport?.phaseAImportKwh || 0}
            peak={meterImportExport?.phaseBImportKwh || 0}
            offPeak={meterImportExport?.phaseCImportKwh || 0}
            isLoading={isFetchingViewMeterImportExport}
            userRole={UserRole.ROLE_METER_READER_USER}
          />
        </Col>
        <Col md={6} xl={3} className="d-flex align-items-stretch mt-2 mt-xl-0">
          <BillInfoCard
            title="Billed Value"
            unitValue={Number(meterTimeOfUse?.totalKwh.toFixed(2)) || 0}
            unitText="Unit"
            billValue={Number(meterTimeOfUse?.billValue.toFixed(2)) || 0}
            billText="LKR"
            valueText="Total Bill"
            icon="request_quote"
            iconColor="#4D5EA0"
            iconBackgroundColor="#EEF9FF"
            isLoading={isFetchingMeterTimeOfUse}
          />
        </Col>
      </Row>
      <Row className="mt-4">
        <Col>
          <div className="bg-white p-3 rounded-3">
            <Row className="justify-content-end">
              <Col className="col-auto">
                <ButtonWithBadge
                  text="Filter"
                  icon="filter_alt"
                  onClick={() => setShowFilter(true)}
                  badgeValue={0}
                />
              </Col>
            </Row>
            {!isFetchingViewMeterHistory ? (
              <BarChart
                data1={dayDaily}
                data2={peakDaily}
                data3={offPeakDaily}
                labels={chartLabels}
                borderColor1="#ECD58A"
                borderColor2="#E79F9F"
                borderColor3="#8ABECC"
                backgroundColor1="#ECD58A"
                backgroundColor2="#E79F9F"
                backgroundColor3="#8ABECC"
                yAxesUnit="unit"
                hasThirdDataSet={true}
                stacked={true}
                borderRadius={50}
                dataLabel1="Day Time"
                dataLabel2="Peak Time"
                dataLabel3="Off Peak Time"
                titleCallback={(tooltipItems: any) => {
                  return tooltipItems[0].dataset.label;
                }}
                isDisplayLegend={true}
                isChartDataAvailable={
                  dayDaily.some((dd) => dd !== 0) ||
                  peakDaily.some((pd) => pd !== 0) ||
                  offPeakDaily.some((opd) => opd !== 0)
                }
              />
            ) : (
              <SpinnerModal
                show={isFetchingViewMeterHistory}
                withOverlay={false}
              />
            )}
          </div>
        </Col>
      </Row>
      <div className="bg-white p-3 rounded-3 mt-4">
        <Row className="align-items-center">
          <Col>
            <Row className="row-cols-auto align-items-center">
              <Col className="font-size-14 text-dark font-weight-500 pe-0">
                Live Meter Date
              </Col>
              <Col>
                <ActiveInactiveIndicator isActive={true} isLive={true} />
              </Col>
            </Row>
          </Col>
          <Col className="col-auto">
            <Row className="row-cols-auto align-items-center text-light font-size-14 font-weight-400">
              <Col className="pe-0">Last Updated:</Col>
              <Col>{moment(lastUpdateDate).format("h:mm:ss A")}</Col>
            </Row>
          </Col>
        </Row>
        <Row className="mt-3 g-3">
          {latestMeterSnapshot?.slice(1).map((lms) => {
            return (
              <Col key={lms.id} className="col-12 col-md-6 col-xl-3">
                <LiveMeterComponent latestMeterSnapshot={lms} />
              </Col>
            );
          })}
        </Row>
      </div>
      <FilterMeterDashboardSidePanel
        showFilter={showFilter}
        setShowFilter={(value: boolean) => {
          setShowFilter(value);
        }}
      />
    </>
  );
};

export default RoleMeterReaderDashboard;
